import { AgGridReact, Card, Flex, Grid, Text } from "@jsq/ds/core";
import { type GetComplianceCaseQuery } from "~/graphql/supergraphTypes";
import { SPACING } from "~/styles/variables";
import { redactAccountNumber } from "~/utils/redact";

interface Props {
  complianceCase: GetComplianceCaseQuery["complianceCase"] | undefined;
}
type SourceOfFundDetail = {
  storedAccountDetail: { name: string; value: any[] };
  incomingPaymentDetail: { value: any[] };
};

const propToRowHeader = { ownerName: "Account owner name", accountNumber: "Account number" };

const redactIfAccountNumber = (rowKey: keyof typeof propToRowHeader, value: string) => {
  return rowKey === "accountNumber" ? redactAccountNumber(value) : value;
};

const displayOnlyIfAccountNumber = (rowKey: keyof typeof propToRowHeader, str: string) => {
  return rowKey === "accountNumber" ? str : null;
};

export const FundComparisonCard = ({ complianceCase }: Props) => {
  if (!complianceCase || !complianceCase.metaData) {
    return null;
  }
  const sourceOfFundsColDefs: AgGridReact.AgGridReactProps["columnDefs"] = [
    {
      field: "rowHeader",
      headerName: "",
    },
    {
      field: "jsqDetails",
      headerName: "Juniper Square bank account details",
      tooltipField: "jsqDetailsTooltip",
    },
    {
      field: "incomingDetails",
      headerName: "Incoming payment details",
      tooltipField: "incomingDetailsTooltip",
    },
  ];
  const gridOptions: AgGridReact.AgGridReactProps["gridOptions"] = {
    autoSizeStrategy: {
      type: "fitGridWidth",
    },
    suppressCellFocus: true,
    domLayout: "autoHeight",
  };

  const sourceOfFundDetails = complianceCase.metaData.source_of_funds_details;

  const rows = sourceOfFundDetails.map((detail: SourceOfFundDetail) => ({
    rowHeader: propToRowHeader[detail.storedAccountDetail.name as keyof typeof propToRowHeader],
    jsqDetails: redactIfAccountNumber(
      detail.storedAccountDetail.name as keyof typeof propToRowHeader,
      detail.storedAccountDetail.value[0],
    ),
    jsqDetailsTooltip: displayOnlyIfAccountNumber(
      detail.storedAccountDetail.name as keyof typeof propToRowHeader,
      detail.storedAccountDetail.value[0],
    ),
    incomingDetails: redactIfAccountNumber(
      detail.storedAccountDetail.name as keyof typeof propToRowHeader,
      detail.incomingPaymentDetail.value[0],
    ),
    incomingDetailsTooltip: displayOnlyIfAccountNumber(
      detail.storedAccountDetail.name as keyof typeof propToRowHeader,
      detail.incomingPaymentDetail.value[0],
    ),
  }));

  return (
    <Card>
      <Flex
        css={{ marginBottom: SPACING.LARGE }}
        alignContent="center"
        alignItems="center"
        justifyContent="space-between"
      >
        <Text variant="secondary-md">SOURCE OF FUNDS COMPARISON</Text>
      </Flex>

      <div style={{ overflow: "hidden" }}>
        <Grid
          gridOptions={gridOptions}
          rowData={rows}
          columnDefs={sourceOfFundsColDefs}
          suppressColumnVirtualisation={true}
          suppressRowVirtualisation={true}
        ></Grid>
      </div>
    </Card>
  );
};
